<script setup lang="ts">
import BButton from '@/components/common/bButton.vue';
import { ref, watch } from 'vue'
import PhMagnifyingGlassThin from '~icons/ph/magnifying-glass-thin'
import { onMounted, onUnmounted } from 'vue';
import { motion } from "motion-v";
import { useMagicKeys, watchDebounced, onClickOutside } from '@vueuse/core';
import { useTemplateRef } from 'vue';
import { projectApi, evaluationApi, invoicingProjectsApi, recruitmentApi, resourceApi } from '@/services/api';
import { useI18n } from 'vue-i18n';
import ResourceItem from '@/components/search/ResourceItem.vue'
import ProjectItem from '@/components/search/ProjectItem.vue'
import InvoiceItem from '@/components/search/InvoiceItem.vue'
import EvaluationItem from '@/components/search/EvaluationItem.vue'
import CandidateItem from '@/components/search/CandidateItem.vue'
import SearchModuleDisplay from '@/components/application/searchCommand/SearchModuleDisplay.vue';
import { useUserStore } from '@/config/store/userStore'
import { isModuleActive } from '@/services/application'

const userStore = useUserStore()

const { t } = useI18n()

const isAtLeastLoadedOneTime = ref(false);

const searchPopupRef = useTemplateRef('searchPopupRef');

onClickOutside(
  searchPopupRef,
  () => {
    closeSearch();
  },
  { ignore: ['.ignore-global-search-close'] },
)

const keys = useMagicKeys()
const shiftF = navigator.userAgent.toLowerCase().includes('mac') ? keys['MetaLeft+ShiftLeft+F'] : keys['ControlLeft+ShiftLeft+F'];

const projectRequest = projectApi.globalSearch()
const invoiceRequest = invoicingProjectsApi.globalSearch()
const evaluationRequest = evaluationApi.globalSearch()
const recruitmentRequest = recruitmentApi.globalSearch()
const resourceRequest = resourceApi.globalSearch()
const alumniRequest = resourceApi.globalSearchAlumni()

const searchList = ref({ resources: [], alumni: [], projects: [], invoices: [], evaluations: [], recruitmentProcesses: [] })
const searchTotals = ref({ resources: 0, alumni: 0, projects: 0, invoices: 0, evaluations: 0, recruitmentProcesses: 0 })
const loadingMore = ref({ resources: false, alumni: false, projects: false, invoices: false, evaluations: false, recruitmentProcesses: false })
const PAGE_SIZE = 5

watch(shiftF, async () => {
  openSearch()
})

const searchQuery = ref('')
const isOpen = ref(false)

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && isOpen.value) {
    isOpen.value = false;
  }
};

const openSearch = () => {
  isOpen.value = true;
};

const closeSearch = () => {
  isOpen.value = false;
};

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});

watchDebounced(searchQuery, (v) => {
  const query = { searchString: v }

  searchList.value = { resources: [], alumni: [], projects: [], invoices: [], evaluations: [], recruitmentProcesses: [] }
  searchTotals.value = { resources: 0, alumni: 0, projects: 0, invoices: 0, evaluations: 0, recruitmentProcesses: 0 }

  resourceRequest.refresh({ query }).then((res) => {
    searchList.value.resources = res.resources
    searchTotals.value.resources = res.total ?? res.resources.length
  })
  alumniRequest.refresh({ query: { display: v, page: 0, limit: PAGE_SIZE } }).then((res) => {
    searchList.value.alumni = res.docs
    searchTotals.value.alumni = res.count?.total ?? res.docs.length
  })
  projectRequest.refresh({ query }).then((res) => {
    searchList.value.projects = res.projects
    searchTotals.value.projects = res.total ?? res.projects.length
  })
  invoiceRequest.refresh({ query }).then((res) => {
    searchList.value.invoices = res.invoices
    searchTotals.value.invoices = res.total ?? res.invoices.length
  })
  evaluationRequest.refresh({ query }).then((res) => {
    searchList.value.evaluations = res.evaluations
    searchTotals.value.evaluations = res.total ?? res.evaluations.length
  })
  recruitmentRequest.refresh({ query }).then((res) => {
    searchList.value.recruitmentProcesses = res.recruitmentProcesses
    searchTotals.value.recruitmentProcesses = res.total ?? res.recruitmentProcesses.length
  })
  isAtLeastLoadedOneTime.value = true;
}, { debounce: 350 })

const loadMoreResources = async () => {
  loadingMore.value.resources = true
  try {
    const res = await resourceRequest.refresh({ query: { searchString: searchQuery.value, skip: searchList.value.resources.length, limit: PAGE_SIZE } })
    searchList.value.resources = [...searchList.value.resources, ...res.resources]
  }
  finally {
    loadingMore.value.resources = false
  }
}

const loadMoreAlumni = async () => {
  loadingMore.value.alumni = true
  try {
    const res = await alumniRequest.refresh({
      query: {
        display: searchQuery.value,
        page: Math.floor(searchList.value.alumni.length / PAGE_SIZE),
        limit: PAGE_SIZE
      }
    })
    searchList.value.alumni = [...searchList.value.alumni, ...res.docs]
  }
  finally {
    loadingMore.value.alumni = false
  }
}

const loadMoreProjects = async () => {
  loadingMore.value.projects = true
  try {
    const res = await projectRequest.refresh({ query: { searchString: searchQuery.value, skip: searchList.value.projects.length, limit: PAGE_SIZE } })
    searchList.value.projects = [...searchList.value.projects, ...res.projects]
  }
  finally {
    loadingMore.value.projects = false
  }
}

const loadMoreInvoices = async () => {
  loadingMore.value.invoices = true
  try {
    const res = await invoiceRequest.refresh({ query: { searchString: searchQuery.value, skip: searchList.value.invoices.length, limit: PAGE_SIZE } })
    searchList.value.invoices = [...searchList.value.invoices, ...res.invoices]
  }
  finally {
    loadingMore.value.invoices = false
  }
}

const loadMoreEvaluations = async () => {
  loadingMore.value.evaluations = true
  try {
    const res = await evaluationRequest.refresh({ query: { searchString: searchQuery.value, skip: searchList.value.evaluations.length, limit: PAGE_SIZE } })
    searchList.value.evaluations = [...searchList.value.evaluations, ...res.evaluations]
  }
  finally {
    loadingMore.value.evaluations = false
  }
}

const loadMoreRecruitmentProcesses = async () => {
  loadingMore.value.recruitmentProcesses = true
  try {
    const res = await recruitmentRequest.refresh({ query: { searchString: searchQuery.value, skip: searchList.value.recruitmentProcesses.length, limit: PAGE_SIZE } })
    searchList.value.recruitmentProcesses = [...searchList.value.recruitmentProcesses, ...res.recruitmentProcesses]
  }
  finally {
    loadingMore.value.recruitmentProcesses = false
  }
}

</script>

<template>
  <VDropdown placement="bottom" no-auto-focus v-model:shown="isOpen"
    class="w-full max-w-full" :autoHide="false" :triggers="[]" :hideTriggers="[]">
    <div class="relative hidden md:block ignore-global-search-close w-full">
      <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
        <PhMagnifyingGlassThin class="subtitle w-5 h-5" />
      </div>
      <input
        v-model="searchQuery"
        @focus="openSearch"
        class="w-full bg-transparent text-sm card-base dark:border-gray-600 py-2 pl-10 pr-24 transition duration-300 ease focus:outline-hidden focus:border-primary-600 shadow-sm focus:shadow-xl"
        :placeholder="t('components.search.searchCommande.searchAnythingPlaceholder')"
      />
    </div>

    <BButton bg="base" class="w-12 h-12 p-0! rounded-full! justify-center! md:hidden ignore-global-search-close"
      @click="openSearch">
      <PhMagnifyingGlassThin class="w-5 h-5" />
    </BButton>

    <template #popper="{ shown }">
      <motion.div
        v-if="shown"
        ref="searchPopupRef"
        class="card-base max-h-[90dvh] w-[98vw] max-w-[100vw] overflow-hidden flex flex-col min-h-[250px] overflow-y-auto xl:w-[1000px] md:w-[800px]"
      >
        <div class="relative block md:hidden">
          <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <PhMagnifyingGlassThin class="subtitle w-5 h-5" />
          </div>
          <input
            v-model="searchQuery"
            class="w-full bg-transparent text-sm card-base pl-10 transition duration-300 ease focus:outline-hidden focus:border-primary-600 shadow-sm focus:shadow-xl"
            :placeholder="t('components.search.searchCommande.searchAnythingPlaceholder')"
          />
        </div>

        <div
          v-if="!isAtLeastLoadedOneTime"
          class="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center"
        >
          <PhMagnifyingGlassThin class="w-8 h-8 subtitle opacity-60" />
          <p class="text-sm subtitle max-w-sm">
            {{ t('components.search.searchCommande.searchAnythingPlaceholder') }}
          </p>
        </div>

        <div class="flex flex-col gap-4 px-2 pb-3" v-if="isAtLeastLoadedOneTime">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SearchModuleDisplay :list="searchList.resources"
              :title="t('components.search.searchCommande.activeResources')"
              :not-found="t('components.search.searchCommande.noResourceFound')" :is-loading="resourceRequest.loading"
              :total="searchTotals.resources" :is-loading-more="loadingMore.resources" @load-more="loadMoreResources">
              <template #default="{ item }">
                <ResourceItem :resource="item" @close-search="closeSearch" />
              </template>
            </SearchModuleDisplay>

            <SearchModuleDisplay :list="searchList.alumni"
              :title="t('components.search.searchCommande.activeAlumni')"
              :not-found="t('components.search.searchCommande.noAlumniFound')" :is-loading="alumniRequest.loading"
              :total="searchTotals.alumni" :is-loading-more="loadingMore.alumni" @load-more="loadMoreAlumni">
              <template #default="{ item }">
                <ResourceItem :resource="item" @close-search="closeSearch" />
              </template>
            </SearchModuleDisplay>
          </div>

          <SearchModuleDisplay v-if="isModuleActive(userStore.user.firm, 5)" :list="searchList.projects" :title="t('components.search.searchCommande.activeProjects')"
            :not-found="t('components.search.searchCommande.noProjectFound')" :is-loading="projectRequest.loading"
            :total="searchTotals.projects" :is-loading-more="loadingMore.projects" @load-more="loadMoreProjects">
            <template #default="{ item }">
              <ProjectItem :project="item" @close-search="closeSearch" />
            </template>
          </SearchModuleDisplay>

          <SearchModuleDisplay v-if="isModuleActive(userStore.user.firm, 19)" :list="searchList.invoices" :title="t('components.search.searchCommande.activeInvoices')"
            :not-found="t('components.search.searchCommande.noInvoiceFound')" :is-loading="invoiceRequest.loading"
            :total="searchTotals.invoices" :is-loading-more="loadingMore.invoices" @load-more="loadMoreInvoices">
            <template #default="{ item }">
              <InvoiceItem :invoice="item" @close-search="closeSearch" />
            </template>
          </SearchModuleDisplay>

          <SearchModuleDisplay v-if="isModuleActive(userStore.user.firm, 7)" :list="searchList.evaluations"
            :title="t('components.search.searchCommande.activeEvaluations')"
            :not-found="t('components.search.searchCommande.noEvaluationFound')"
            :is-loading="evaluationRequest.loading" :total="searchTotals.evaluations"
            :is-loading-more="loadingMore.evaluations" @load-more="loadMoreEvaluations">
            <template #default="{ item }">
              <EvaluationItem :evaluation="item" @close-search="closeSearch" />
            </template>
          </SearchModuleDisplay>

          <SearchModuleDisplay v-if="isModuleActive(userStore.user.firm, 4)" :list="searchList.recruitmentProcesses"
            :title="t('components.search.searchCommande.activeCandidates')"
            :not-found="t('components.search.searchCommande.noCandidateFound')"
            :is-loading="recruitmentRequest.loading" :total="searchTotals.recruitmentProcesses"
            :is-loading-more="loadingMore.recruitmentProcesses" @load-more="loadMoreRecruitmentProcesses">
            <template #default="{ item }">
              <CandidateItem :recruitment-process="item" @close-search="closeSearch" />
            </template>
          </SearchModuleDisplay>

        </div>
      </motion.div>
    </template>
  </VDropdown>

</template>

<style>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  backdrop-filter: blur(5px);
  z-index: 100;
}
</style>
